import {
  FieldRow,
  Form,
  ImageMarkerType,
  LookupOption,
  SingleImageMarkerBox,
} from '@/components';
import { useDictaphone } from '@/components/Dictaphone';
import { GeoItemPosition, SpecialRequestStatus } from '@/core';
import {
  AddSpecialRequestArgs,
  UpdateSpecialRequestArgs,
  useGetProjectHomeInfoQuery,
} from '@/redux';
import { useFlippoBlobUrl } from '@/utils/flippo';
import {
  SpecialRequestPdfDownloadButton,
  useCategoryLookupSource,
  useCommunityContext,
  useProjectStepLookupSource,
} from '@/views';
import { Button, Col, Input, Modal, Row } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { SpecialRequestCommentsPanel } from './SpecialRequestCommentsPanel';
import styles from './SpecialRequestDetailsModal.module.scss';
import { SpecialRequestStatusesList } from './SpecialRequestStatusesList';

interface Props<
  TValue extends AddSpecialRequestArgs | UpdateSpecialRequestArgs,
> {
  title: React.ReactNode;
  stepId?: string;
  projectId: string;
  homeId: string;
  onSubmit: (args: TValue) => any;
  onClose: () => any;
  value: TValue | undefined;
  isEdit?: boolean;
  isFloorplan?: boolean;
  roomId?: string;
  roomName?: string;
  preloadValue?: LookupOption;
  disabledCategory?: boolean;
  position?: GeoItemPosition;
  number?: number;
  disableDescription?: boolean;
  readonly?: boolean;
}

const schema = Yup.object().shape({
  name: Yup.string().field().nullable().required(),
  stepId: Yup.string().field().nullable().required(),
  categoryId: Yup.string().field().nullable().required(),
  status: Yup.string().field().nullable().required(),
  description: Yup.string().nullable(),
  constructionDescription: Yup.string().nullable(),
  fee: Yup.bool().required(),
});

function useFloorplan(props: {
  projectId: string;
  homeId: string;
  position?: GeoItemPosition;
}) {
  const { homeId, projectId, position } = props;
  const { data: projectHomeInfo } = useGetProjectHomeInfoQuery(
    {
      id: projectId,
      homeId,
    },
    { skip: !position },
  );
  const { url: floorplanImageUrl } = useFlippoBlobUrl(
    projectHomeInfo?.floorplanImageReference,
  );
  return { floorplanId: projectHomeInfo?.floorplanId, floorplanImageUrl };
}

export function SpecialRequestDetailsModal<
  TValue extends AddSpecialRequestArgs | UpdateSpecialRequestArgs,
>(props: Props<TValue>) {
  const {
    title,
    onSubmit,
    projectId,
    onClose,
    value,
    isEdit,
    preloadValue,
    disabledCategory,
    position,
    number,
    disableDescription,
    roomId,
    roomName,
    readonly,
  } = props;
  const { t } = useTranslation();
  const stepsDataSource = useProjectStepLookupSource([projectId]);

  const { communityId } = useCommunityContext();
  const categoriesDataSource = useCategoryLookupSource({
    communityId,
    listed: true,
  });

  const { floorplanImageUrl } = useFloorplan(props);

  const id: string | undefined = (value as UpdateSpecialRequestArgs).id;
  const dictaphoneProps = useDictaphone();

  return (
    <Modal
      width="auto"
      style={{ maxWidth: '1200px' }}
      title={title}
      visible
      onCancel={onClose}
      footer={
        !readonly && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <div>
              <SpecialRequestPdfDownloadButton specialRequestId={id} />
            </div>

            <div>
              <Button key="back" type="default" onClick={onClose}>
                {t('cancel')}
              </Button>
              <Form.Submit uid="special-request" key="submit" type="primary">
                {t('save')}
              </Form.Submit>
            </div>
          </div>
        )
      }
    >
      <Form.Formik<TValue>
        initialValues={value!}
        uid="special-request"
        onSubmit={onSubmit}
        validationSchema={schema}
        i18n="specialRequests.details"
      >
        <Row>
          <Col flex="3">
            {
              <>
                {position && (
                  <SingleImageMarkerBox
                    marker={{
                      x: position?.x ?? 0,
                      y: position?.y ?? 0,
                      label: value!.name,
                      number: number ?? 1,
                      type: 'star' as ImageMarkerType,
                      hexColor: undefined,
                    }}
                    src={floorplanImageUrl}
                  />
                )}
                <FieldRow>
                  <Col span={12}>
                    <Form.Input
                      name="name"
                      required
                      label={t('specialRequests.details.itemName')}
                      disabled={readonly}
                      enableVoice
                      {...dictaphoneProps}
                    />
                  </Col>
                  <Col span={12}>
                    <Form.AsyncSelect
                      name="stepId"
                      dataSource={stepsDataSource}
                      preload
                      required
                      disabled={readonly}
                    />
                  </Col>
                </FieldRow>
                <FieldRow>
                  <Col span={12}>
                    <Form.AsyncSelect
                      name="categoryId"
                      dataSource={categoriesDataSource}
                      required
                      preloadInitial={preloadValue ?? value?.categoryId}
                      disabled={(disabledCategory ?? value?.asItem) || readonly}
                      allowClear={false}
                    />
                  </Col>
                  <Col span={12}>
                    <div className="ant-row ant-form-item form-item-vertical form-group">
                      <div className="ant-col ant-form-item-label">
                        <label className="ant-form-item-no-colon mb-0">
                          {roomId && t('specialRequests.details.roomName')}
                        </label>
                      </div>
                      {roomId ? (
                        <Input
                          className="ant-col ant-form-item-control"
                          disabled
                          defaultValue={roomName}
                        />
                      ) : (
                        <Form.Checkbox name="fee" disabled={readonly} />
                      )}
                    </div>
                  </Col>
                </FieldRow>
                {roomId && <Form.Checkbox name="fee" disabled={readonly} />}
                <Form.TextArea
                  name="description"
                  label={t('specialRequests.details.addDescription')}
                  disabled={disableDescription || readonly}
                  enableVoice
                  {...dictaphoneProps}
                />
                <Form.TextArea
                  name="constructionDescription"
                  disabled={disableDescription || readonly}
                />
              </>
            }
          </Col>
          <Col className={styles.secondColumn} flex="2">
            {isEdit && (
              <FieldRow>
                <Col span={12}>
                  <Form.EnumSelect
                    name="status"
                    type={SpecialRequestStatus}
                    required
                    disabled={readonly}
                  />
                </Col>
              </FieldRow>
            )}
            <Form.Images
              name="imageActions"
              defer
              compact
              ownerId={id}
              ownerType="SpecialRequestImage"
              noPreload={!id}
              label={t('specialRequests.details.images')}
              readonly={readonly}
            />
            <Form.DeferAttach
              name="attachments"
              noPreload={!id}
              ownerId={id}
              ownerType="SpecialRequestAttachments"
              showActions
              openOnClick
              readonly={readonly}
              className="mb-3"
            />
            {isEdit && (
              <SpecialRequestCommentsPanel
                specialRequestId={id}
                readonly={readonly}
              />
            )}
            <SpecialRequestStatusesList specialRequestId={id} />
          </Col>
        </Row>
      </Form.Formik>
    </Modal>
  );
}
